<script lang="ts" setup>
import { computed } from 'vue'
import { PaymentMethodEnum, formatDiscountLabel } from '../constants'
import { primaryColor } from '@/config/brands'
import paypalIcon from '@/assets/paypal.png'
import stripeIcon from '@/assets/stripe.png'
import paypalDarkIcon from '@/assets/paypal-dark.png'
import stripeDarkIcon from '@/assets/stripe-dark.png'

interface PaymentMethod {
  value: number
  label: string
  icon: string
  originalMethod?: any
}

const props = withDefaults(defineProps<{
  modelValue?: any
  availableMethods: any[]
  variant?: 'default' | 'large'
  dark?: boolean
  align?: 'center' | 'start'
  transparent?: boolean
}>(), {
  modelValue: undefined,
  variant: 'default',
  dark: false,
  align: 'center',
  transparent: false,
})

defineEmits<{
  (e: 'update:model-value', value: number): void
}>()

// 所有支付方式配置
const allPaymentMethods: PaymentMethod[] = [
  {
    value: PaymentMethodEnum.PAYPAL,
    label: 'PayPal',
    icon: paypalIcon,
  },
  {
    value: PaymentMethodEnum.STRIPE,
    label: 'Stripe',
    icon: stripeIcon,
  },
]

// 根据 availableMethods 过滤出可用的支付方式，并保持接口返回的顺序
const paymentMethods = computed<PaymentMethod[]>(() => {
  // 确保是数组后再进行操作
  const methods = props.availableMethods || []

  // 按照接口返回的顺序构建支付方式列表
  return methods
    .map((m) => {
      const methodConfig = allPaymentMethods.find(config => Number(config.value) === Number(m.paymentMethod))
      return methodConfig ? { ...methodConfig, originalMethod: m } : null
    })
    .filter(Boolean) as PaymentMethod[]
})

// 暗色选中态边框色（需作为局部变量才能被 CSS v-bind 正确注入）
const activeBorderColor = primaryColor

// 暗色模式下切换为白色图标
const resolvedPaymentMethods = computed(() =>
  paymentMethods.value.map(m => ({
    ...m,
    icon: props.dark
      ? (m.value === PaymentMethodEnum.PAYPAL ? paypalDarkIcon : stripeDarkIcon)
      : m.icon,
  })),
)
</script>

<template>
  <!-- Default 样式：小的圆形 pill 样式（用于弹窗） -->
  <div v-if="variant === 'default'" class="payment-method-selector" :class="{ dark: props.dark }">
    <div
      v-for="method in resolvedPaymentMethods"
      :key="method.value"
      class="payment-method-btn"
      :class="{ active: Number(props.modelValue) === Number(method.value) }"
      @click="$emit('update:model-value', method.value)"
    >
      <div
        class="py-6px px-32px box-border rounded-999px"
        :class="{
          'bg-white': !props.dark && Number(props.modelValue) === Number(method.value),
          'bg-[#363536]': props.dark && Number(props.modelValue) === Number(method.value),
        }"
      >
        <img :src="method.icon" :alt="method.label" class="payment-icon pop">
      </div>
    </div>
  </div>

  <!-- Large 样式：大按钮样式（用于 pay 和 subscribe 页面） -->
  <div
    v-else
    class="payment-method-selector-large"
    :class="{ dark: props.dark, start: props.align === 'start', transparent: props.transparent }"
  >
    <div
      v-for="method in resolvedPaymentMethods"
      :key="method.value"
      class="payment-method-btn-large"
      :class="{
        active: Number(props.modelValue) === Number(method.value),
        paypal: method.value === PaymentMethodEnum.PAYPAL && Number(props.modelValue) === Number(method.value),
        transparent: props.transparent,
      }"
      @click="$emit('update:model-value', method.value)"
    >
      <img :src="method.icon" :alt="method.label" class="payment-icon-large">
      <div v-if="formatDiscountLabel(method?.originalMethod?.maxDiscountRate)" class="discount-badge-large">
        {{ formatDiscountLabel(method?.originalMethod?.maxDiscountRate) }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Default 样式 */
.payment-method-selector {
  display: flex;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 999px;
  width: fit-content;
  margin: 0 auto 8px auto;
  min-width: 200px;
  margin-top: 16px;
}

.payment-method-btn {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 600;
  border: none;
  background: transparent;
}

.payment-method-btn.active {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.payment-icon {
  height: 20px;
  object-fit: contain;
}
.payment-icon.pop {
  height: 24px;
  min-width: 60px;
  display: block;
}

/* Large 样式 */
.payment-method-selector-large {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 0;
  width: 100%;
  background-color: #fff;
  margin: 0 10px;
  padding: 20px 0;
  box-sizing: border-box;
}

.payment-method-selector-large.start {
  justify-content: flex-start;
  margin: 20px 0 0 0;
}

.payment-method-selector-large.transparent {
  background-color: transparent;
  padding: 20px 0 0 0;
}

.payment-method-btn-large {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  min-width: 100px;
  height: 34px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.payment-method-btn-large.transparent {
  background: transparent;
}

.payment-method-btn-large.active {
  background: #F6F2FFFF;
  border-color: #9875FBFF;
}

.payment-method-btn-large.active.transparent {
  background: transparent;
}

.payment-method-btn-large.active.paypal {
  background: #F6F2FFFF;
  border-color: #9875FBFF;
}

.payment-icon-large {
  height: 20px;
  object-fit: contain;
}

/* Large 样式折扣标签 */
.discount-badge-large {
  position: absolute;
  top: -8px;
  left: -3px;
  right: auto;
  background: linear-gradient(135deg, #F961BCFF 0%, #F74984FF 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 0px 6px;
  border-radius: 8px 0 8px 0;
  white-space: nowrap;
  line-height: 1.3;
  z-index: 10;
  min-width: auto;
  text-align: center;
  transform: scale(0.95);
}

/* Default 暗色主题样式（阅读器弹窗） */
.payment-method-selector.dark {
  background: #2A292A;
}

.payment-method-selector.dark .payment-method-btn.active {
  box-shadow: none;
}

/* Dark 暗色主题样式 */
.payment-method-selector-large.dark {
  background-color: transparent;
}

.payment-method-selector-large.dark .payment-method-btn-large {
  background: #1C1C1E;
  border-color: #3A3A3C;
}

.payment-method-selector-large.dark .payment-method-btn-large.active {
  background: #1C1C1E;
  border-color: v-bind(activeBorderColor);
}
</style>
